import * as React from "react";

import { definedMap } from "@glideapps/ts-necessities";

import { Owner } from "common/Database";
import { formatDateTime, DateFormat } from "common/format-date-time";
import { dontAwait } from "common/Support";
import { previewCharge } from "../../orgs-lib";
import { PreviewChargeResponse } from "common/firebase-function-types";
import { UserHeaderStyle } from "./user-header-style";
import { displayDollars } from "common/app-plans";
import { OwnerKind } from "common/Database/owner-kind";
import { getAppFacilities } from "webapp/lib/app-renderer";
import { showErrorModal, showPaymentModal } from "designer/ui/smart/modals/modals";

interface Props {
    readonly owner: Owner;
}

const UserHeaderImpl: React.FunctionComponent<Props> = p => {
    const { owner } = p;

    const [preview, setPreview] = React.useState<PreviewChargeResponse>();

    React.useEffect(() => {
        const fn = async () => {
            const r = await previewCharge({
                organizationID: owner.kind === OwnerKind.Organization ? owner.id : undefined,
            });
            setPreview(r);
        };
        dontAwait(fn());
    }, [owner.kind, owner.id]);

    const monthlySub = owner.monthlySubscription;
    const annualSub = owner.annualSubscription;

    const nextMonthlyRenewalDate = definedMap(monthlySub?.nextInvoice, x => new Date(x.year, x.month - 1, x.day));
    const nextAnnualRenewalDate = definedMap(annualSub?.nextInvoice, x => new Date(x.year, x.month - 1, x.day));

    const nextAnyRenewalDate =
        ((nextMonthlyRenewalDate ?? new Date(Number.MAX_SAFE_INTEGER)) <=
        (nextAnnualRenewalDate ?? new Date(Number.MAX_SAFE_INTEGER))
            ? nextMonthlyRenewalDate
            : nextAnnualRenewalDate) ?? nextMonthlyRenewalDate;

    const nextMonthlyRenewal = definedMap(nextMonthlyRenewalDate, x => formatDateTime(x, DateFormat.Medium, undefined));
    const nextAnnualRenewal = definedMap(nextAnnualRenewalDate, x => formatDateTime(x, DateFormat.Medium, undefined));
    const nextAnyRenewal = definedMap(nextAnyRenewalDate, x => formatDateTime(x, DateFormat.Short, undefined));

    function makeLineItem(left: string, right: React.ReactElement | string | undefined): React.ReactNode {
        if (right === undefined) return null;
        return (
            <>
                <div className="uhs-left">{left}</div>
                <div className="uhs-right">{right}</div>
            </>
        );
    }

    const currentBalance = preview?.monthly?.currentBalance;

    const getAndUpdatePayment = async () => {
        const setupResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
            ownerID: owner.id,
        });
        if (setupResponse === undefined || setupResponse.status !== 402) {
            return await showErrorModal("Something went wrong updating payment information");
        }
        const setupIntentClientSecret: string = (await setupResponse.json()).setupIntentClientSecret;

        /**
         * We're not actually paying anything, we're just updating the payment method.
         * The estimate of 0 dollars should be reasonable.
         */
        const paymentRequestInfo = {
            estimateDollars: 0,
            label: "Update payment method",
        };
        const paymentMethodID = await showPaymentModal(setupIntentClientSecret, paymentRequestInfo);
        if (typeof paymentMethodID === "string") {
            const attachResponse = await getAppFacilities().callAuthCloudFunction("updateUnifiedPaymentInformation", {
                ownerID: owner.id,
                paymentMethodID,
            });
            if (attachResponse === undefined || !attachResponse.ok) {
                return await showErrorModal("Something went wrong updating payment information");
            }
        }
    };

    return (
        <UserHeaderStyle>
            <div className="uhs-col">
                {makeLineItem("Monthly Renewal", nextMonthlyRenewal)}
                {makeLineItem("Yearly Renewal", nextAnnualRenewal)}
                {currentBalance !== undefined &&
                    makeLineItem(
                        currentBalance > 0 ? "Account Balance" : "Account Credit",
                        displayDollars(Math.abs(currentBalance) / 100)
                    )}
                {makeLineItem(
                    "Next Payment (estimate)",
                    definedMap(preview?.monthly?.amountDue, n => `${displayDollars(n / 100)} on ${nextAnyRenewal}`)
                )}
                {makeLineItem(
                    "Payment Method",
                    <button onClick={getAndUpdatePayment}>
                        {definedMap(preview?.monthly?.cardPreview.last4, x =>
                            x === "" ? "Unknown credit card" : `Card ending ${x}`
                        ) ?? "Add payment method"}
                    </button>
                )}

                {preview?.portalUrl !== undefined && (
                    <>
                        <span className="uhs-left">Payment History:</span>
                        <a className="uhs-right" href={preview.portalUrl}>
                            Customer Portal
                        </a>
                    </>
                )}
            </div>
        </UserHeaderStyle>
    );
};

export default UserHeaderImpl;
